// DonationBanner — the Wikipedia-style funding pledge ("Keep Nitter
// alive!"), rendered as a quiet inline card on every SPA screen: mounted
// under the sticky header on the dashboard AND the data-source page, above
// that page's content. (The original app mounted it in App above the routed
// content behind a fixed navbar; here the header is sticky and in-flow, so
// each page mounts the card directly after its <Header> — same visual slot,
// and navigating between the pages remounts the component, which resets the
// session dismissal exactly like the original App-level key={pathname}.)
// The visible pledge content (head + copy + progress + stats + CTA) is the
// reusable DonationDriveCard component; this wrapper owns the sticky/fold/
// close chrome around it.
//
//   ♥  Keep Nitter alive!  The next $147 in server bills is due Oct 26.
//   Servers, bandwidth, and the work of keeping these instances
//   reachable all cost money and effort. …
//   ▓▓▓▓▓▓▓▓▓▓▓░░░░░
//   Raised $119 of $147 (81%) so far    [PP] Donate $5 with PayPal
//
// Behavior:
//   - The drive (target, deadline, progress) comes from lib/donate.ts: a
//     deterministic curve, optionally overridden per browser (nitter:donate-
//     drive). The banner subscribes to updates, so a background refresh
//     (or an edit from another tab) re-renders it in place.
//   - The card is position:sticky — it docks just below the sticky header
//     while you scroll (the header's live height is measured on mount and
//     on resize; the header is in-flow, so no body-class clearance dance is
//     needed). Once its flow slot has fully scrolled out of view it FOLDS
//     into a slim one-line strip (heart · label · progress · stats) that
//     taps to re-expand; it unfolds again near the top of the page.
//   - The close button only hides the card for the CURRENT page view. The
//     dismissal is deliberately NOT persisted: navigating remounts the
//     component, so the pledge returns until the visitor clicks a Donate
//     CTA — which records the click (an intent signal, NOT a completed
//     donation — there is no PayPal return callback here) and the card
//     backs off for DONATE_BACKOFF_MS (two weeks), then quietly returns in
//     case the donation never went through.
//
// Why the fold latch looks the way it does: folding shrinks the page by the
// card's height, and the browser's scroll anchoring compensates by shifting
// scrollY by that same amount. Any fold rule keyed on raw scrollY must
// therefore survive its own shift — engaging only after the card's flow slot
// is entirely above the viewport guarantees the post-shift position stays
// well above the release threshold (no engage → shift → release → engage
// thrash), and a hysteresis band holds the state in between.

import { useEffect, useRef, useState } from "preact/hooks";
import { DonationDriveCard } from "@/components/DonationDriveCard";
import {
  getDrive,
  onFundingState,
  syncDriveFromApi,
  usd,
} from "@/lib/donate";
import type { FundingState } from "@/lib/donate";
import { isDonateBackoff } from "@/settings";
import "./donate.css";

/** Release the fold when scrolled back this close to the top (px). */
const UNFOLD_THRESHOLD = 56;
/** Extra px beyond the card's own height before the fold may ENGAGE. The
 *  card's flow slot sits at the very top of the page; once scrollY exceeds
 *  slot height + slack, the whole slot is above the viewport, so the
 *  post-fold scroll-anchoring shift can never drop scrollY back under
 *  UNFOLD_THRESHOLD (which would make the fold undo itself and thrash). */
const FOLD_SLACK = 80;
/** Fallback card height if the live measurement is unavailable. */
const FALLBACK_CARD_HEIGHT = 180;

export function DonationBanner() {
  // Visitors inside a post-click backoff window never see the card; everyone
  // else gets the (stable, persisted) drive for this browser.
  const [drive, setDrive] = useState<FundingState | null>(() =>
    isDonateBackoff() ? null : getDrive(),
  );
  // Session-local dismissal: resets on every navigation via the per-page
  // remount. NOT written to localStorage on purpose.
  const [closed, setClosed] = useState(false);
  // Sticky fold latch: true while the card is folded into its slim strip.
  const [folded, setFolded] = useState(false);
  // Ref mirror for the passive scroll listener (hysteresis needs the previous
  // latch state on every frame without resubscribing).
  const foldedRef = useRef(false);
  const bannerRef = useRef<HTMLElement | null>(null);
  // Manual expand from the folded strip.
  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(false);

  // Stale-while-revalidate the drive, and subscribe to live updates. Runs
  // once per mount (the banner remounts per page).
  useEffect(() => {
    if (isDonateBackoff()) return;
    let alive = true;
    void syncDriveFromApi();
    const off = onFundingState((state) => {
      if (alive) setDrive(state);
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  // Dock the card just below the page's sticky header (both SPA pages use
  // the same .header). Measured live so the mobile layout (which may wrap
  // or resize the header) keeps the card flush under it.
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const apply = () => {
      const header = document.querySelector<HTMLElement>(".header");
      el.style.top = `${(header?.offsetHeight ?? 0) + 8}px`;
    };
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  // Fold latch, rAF-throttled. Engages only past slot height + slack (see
  // FOLD_SLACK); releases near the top.
  useEffect(() => {
    if (isDonateBackoff()) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        const cardHeight =
          bannerRef.current?.offsetHeight ?? FALLBACK_CARD_HEIGHT;
        const next = foldedRef.current
          ? y > UNFOLD_THRESHOLD
          : y > cardHeight + FOLD_SLACK;
        if (next !== foldedRef.current) {
          foldedRef.current = next;
          setFolded(next);
        }
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const expand = () => {
    expandedRef.current = true;
    setExpanded(true);
  };

  // A manual expand ends on REAL scroll intent (wheel, touchmove, scroll
  // keys) — never on scrollY travel: expanding shifts the page height and
  // scroll anchoring compensates by moving scrollY by the card's own height,
  // which would instantly trip any travel-based release.
  useEffect(() => {
    const release = () => {
      if (!expandedRef.current) return;
      expandedRef.current = false;
      setExpanded(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // Only bare scrolling keys, not typing in the search box etc.
      if (e.target !== document.body) return;
      if (
        e.key === " " ||
        e.key === "PageDown" ||
        e.key === "ArrowDown" ||
        e.key === "End"
      ) {
        release();
      }
    };
    window.addEventListener("wheel", release, { passive: true });
    window.addEventListener("touchmove", release, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("wheel", release);
      window.removeEventListener("touchmove", release);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  if (!drive || closed) return null;

  const pct = Math.min(
    100,
    Math.max(1, Math.round((drive.raised / drive.target) * 100)),
  );
  const isFolded = folded && !expanded;

  return (
    <aside
      ref={bannerRef}
      class={`donate-drive${isFolded ? " is-folded" : ""}`}
      role="region"
      aria-label="Instance funding drive"
    >
      <button
        type="button"
        class="donate-drive-close"
        aria-label="Dismiss funding message"
        title="Dismiss — it will be back on your next visit unless you donate"
        onClick={() => setClosed(true)}
      >
        <span class="icon-close" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </span>
      </button>

      {/* Full card. The grid-track collapse (see donate.css) shrinks it to
          zero height when folded; visibility flips after the transition so
          screen readers and focus leave gracefully. The inner content is
          the shared DonationDriveCard. */}
      <div class="donate-drive-full">
        <div class="donate-drive-full-clip">
          <DonationDriveCard drive={drive} />
        </div>
      </div>

      {/* Folded strip — the same pledge in one line; tapping re-expands.
          Only rendered (display:flex) while the card is folded, so it never
          competes with the full card for tab stops. */}
      <button
        type="button"
        class="donate-drive-mini"
        onClick={expand}
        aria-expanded={!isFolded}
        title="Expand the funding pledge"
      >
        <span class="donate-drive-badge" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round">
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
          </svg>
        </span>
        <span class="donate-drive-mini-label">Keep Nitter alive</span>
        <span class="donate-drive-mini-bar" aria-hidden="true">
          <span style={`width:${pct}%`} />
        </span>
        <span class="donate-drive-mini-stats">
          {pct}%
          <span class="donate-drive-mini-amounts">
            {" "}
            · {usd(drive.raised)} of {usd(drive.target)}
          </span>
        </span>
        <svg class="donate-drive-mini-chevron" aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
    </aside>
  );
}
